/**
 * Contrat et outils de `<ReassignRolesFields>` — séparés du composant pour le fast-refresh
 * (`react-refresh/only-export-components` : un fichier de composant n'exporte que des
 * composants).
 *
 * La règle qu'ils portent tient en une phrase : **`null` veut dire « ne pas changer »,
 * jamais « personne »**. Une réaffectation en masse n'envoie que les rôles CHOISIS ; un rôle
 * laissé sur « Ne pas changer » n'apparaît pas dans le corps de la requête, et le serveur le
 * garde en place.
 */

/** Un rôle qu'on peut réaffecter : sa clé (le NOM DU CHAMP envoyé au serveur), son libellé
 *  déjà traduit, une aide facultative affichée sous la liste, et — à l'unité — qui le tient
 *  AUJOURD'HUI (`actuel`, phrase déjà traduite : « Actuellement : Ada Martin »). « Ne pas
 *  changer » ne dit pas ce qu'on garde ; sans cette ligne, on réaffecte à l'aveugle. */
export interface RoleAReaffecter {
    cle: string
    libelle: string
    aide?: string
    actuel?: string
}

/** Une personne qu'on peut désigner. `nom` est déjà prêt à afficher. */
export interface PersonneDesignable {
    id: string
    nom: string
}

/** `{clé du rôle: identifiant de la personne}` — `null` = « Ne pas changer ». */
export type ValeursDesRoles = Record<string, string | null>

/**
 * La valeur du `Select` pour « Ne pas changer ». Radix refuse `''` comme valeur d'option (la
 * chaîne vide est réservée à l'effacement) : il faut un jeton, et il ne doit jamais partir au
 * serveur — `rolesChoisis` le filtre, comme `null`.
 */
export const NE_PAS_CHANGER = '__ne_pas_changer__'

/** Toutes les clés à « Ne pas changer » : l'état d'ouverture d'une modale de réaffectation. */
export function valeursInitiales(roles: readonly RoleAReaffecter[]): ValeursDesRoles {
    return Object.fromEntries(roles.map((role) => [role.cle, null]))
}

/**
 * Les SEULS rôles choisis, prêts à fusionner dans le corps de la requête. Vide = aucun rôle :
 * le bouton de validation reste éteint, et aucun aperçu ne part.
 */
export function rolesChoisis(valeurs: ValeursDesRoles): Record<string, string> {
    const choisis: Record<string, string> = {}
    for (const [cle, personne] of Object.entries(valeurs)) {
        if (personne && personne !== NE_PAS_CHANGER) choisis[cle] = personne
    }
    return choisis
}

/**
 * Les personnes dans l'ordre où on les CHERCHE : par nom, selon la langue de l'écran.
 *
 * Le serveur les rend par IDENTIFIANT (`ORDER BY u.id` : sa pagination est un curseur sur
 * l'identifiant), c'est-à-dire dans l'ordre de création des comptes — un ordre qui ne veut
 * rien dire pour qui cherche « Martin » parmi soixante noms. `Intl.Collator` et non `<` :
 * « Élodie » se range avec les E, pas après le Z.
 */
export function parNom<T extends PersonneDesignable>(
    personnes: readonly T[],
    langue?: string,
): T[] {
    const collation = new Intl.Collator(langue, { sensitivity: 'base' })
    return [...personnes].sort((a, b) => collation.compare(a.nom, b.nom))
}
